import React, { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Menu, X, RefreshCw, HelpCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { useVersionCheck } from "@/hooks/useVersionCheck";
import { HelpProvider, useHelp } from "@/help/HelpContext";
import { getArticleSlugForRoute } from "@/help/articles/index";
import HelpDrawer from "@/help/HelpDrawer";
import { OfflineProvider } from "@/context/OfflineContext";
import { hasClientPrivacy, privacyLabels } from "@/lib/client-privacy";
import OfflineBanner from "@/components/OfflineBanner";
import StaffSidebar from "@/components/StaffSidebar";
import { TooltipProvider } from "@/components/ui/tooltip";

const SIDEBAR_PREFERENCE = "foodbank.desktopSidebarCollapsed";
function readSidebarPreference() {
  try { return window.localStorage.getItem(SIDEBAR_PREFERENCE) === "true"; }
  catch { return false; }
}

const REDISPLAY_DELAY = 5 * 60 * 1000; // 5 minutes

function StaffLayoutInner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarPreference);
  useEffect(() => {
    try { window.localStorage.setItem(SIDEBAR_PREFERENCE, String(sidebarCollapsed)); }
    catch { /* A display preference must not make navigation depend on storage. */ }
  }, [sidebarCollapsed]);
  const { openHelp } = useHelp();

  function handleHelpClick() {
    const slug = getArticleSlugForRoute(location.pathname);
    openHelp(slug);
  }
  const updateAvailable = useVersionCheck();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const redisplayTimer = useRef(null);

  const showBanner = updateAvailable && !bannerDismissed;

  function handleDismiss() {
    setBannerDismissed(true);
    redisplayTimer.current = setTimeout(() => setBannerDismissed(false), REDISPLAY_DELAY);
  }

  useEffect(() => () => clearTimeout(redisplayTimer.current), []);

  const accessDenied = location.state?.accessDenied;
  const deniedPage = location.state?.deniedPage;

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <TooltipProvider delayDuration={150}>
    <div className="min-h-screen flex" data-sidebar-collapsed={sidebarCollapsed}
         style={{ background: "var(--fb-bg)", "--staff-sidebar-width": sidebarCollapsed ? "4.5rem" : "16rem" }}>
      {/* Desktop sidebar — fixed, full viewport height */}
      <aside id="desktop-sidebar" data-testid="desktop-sidebar"
             className="staff-desktop-sidebar hidden lg:flex border-r flex-col fixed top-0 left-0 h-screen z-30"
             style={{ borderColor: "var(--fb-border)", background: "#FBFAF7" }}>
        <StaffSidebar user={user} onLogout={handleLogout} onHelp={handleHelpClick}
                      collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(value => !value)} />
      </aside>

      {/* Mobile/Tablet drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40" onClick={() => setDrawerOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <aside
            className="absolute left-0 top-0 bottom-0 w-72 flex flex-col"
            style={{ borderRight: "1px solid var(--fb-border)", background: "#FBFAF7" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-drawer"
          >
            <StaffSidebar user={user} onLogout={handleLogout} onHelp={handleHelpClick}
                          onLinkClick={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main — offset by sidebar width on desktop */}
      <main className="staff-main flex-1 min-w-0 overflow-x-hidden">
        {/* Offline / sync banner */}
        <OfflineBanner />

        {/* Update available banner */}
        {showBanner && (
          <div
            data-testid="update-banner"
            className="flex items-center justify-between gap-3 px-4 sm:px-6 py-2.5 text-sm font-medium sticky top-0 z-30"
            style={{ background: "#FEF3C7", borderBottom: "1px solid #FDE68A", color: "#92400E" }}
          >
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 shrink-0" />
              <span>A new version is available —</span>
              <button
                data-testid="update-refresh-btn"
                onClick={() => window.location.reload()}
                className="font-semibold underline underline-offset-2 hover:opacity-75"
              >
                Refresh
              </button>
            </div>
            <button
              data-testid="update-dismiss-btn"
              onClick={handleDismiss}
              aria-label="Dismiss update notice"
              className="p-1 rounded hover:bg-black/10 shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b sticky top-0 z-20"
                style={{ background: "#FBFAF7", borderColor: "var(--fb-border)" }}>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setDrawerOpen(true)}
            className="p-2 -ml-2 rounded-lg hover:bg-stone-200"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Logo size={28} />
            <div className="font-bold text-sm" style={{ fontFamily: "Outfit" }}>Pullman Child Welfare</div>
          </div>
          <button
            onClick={handleHelpClick}
            className="p-2 -mr-2 rounded-lg hover:bg-stone-200"
            aria-label="Open help"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
        </header>

        <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-10 page-enter">
          {accessDenied && (
            <div className="mb-5 flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium"
                 style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" }}
                 data-testid="access-denied-banner">
              <span>You don't have access to that page{deniedPage ? ` (${deniedPage})` : ""}.</span>
            </div>
          )}
          {hasClientPrivacy(user) && (
            <div className="fb-status-warning rounded-lg p-3 text-sm mb-4" data-testid="client-privacy-banner">
              Client privacy: {privacyLabels(user).join(", ")} are hidden throughout the app and exports.
              Private notes are hidden too. Client profiles are read-only, and offline client access is disabled.
            </div>
          )}
          <Outlet />
        </div>
      </main>
      <HelpDrawer />
    </div>
    </TooltipProvider>
  );
}

export default function StaffLayout() {
  return (
    <OfflineProvider>
      <HelpProvider>
        <StaffLayoutInner />
      </HelpProvider>
    </OfflineProvider>
  );
}
